/**
 * Created by katarinababic on 23.6.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Theme } from '../../Theme/Theme';
import { Dayjs } from 'dayjs';

const Container = styled.div`
  display: flex;
  align-items: stretch;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 100px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);

    .suggestion-image {
      transform: scale(1.05);
    }

    .arrow-icon {
      transform: translateX(4px);
      opacity: 1;
    }
  }
`;

const ImageContainer = styled.div`
  width: 100px;
  min-width: 100px;
  position: relative;
  overflow: hidden;
`;


const Thumb = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const PlaceholderImage = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, ${Theme.Colors.primary}25 0%, ${Theme.Colors.primary}10 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Theme.Colors.primary};
  font-size: 28px;
`;

const Content = styled.div`
  flex: 1;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`;

const TypeTag = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #00dda2;
  margin-bottom: 6px;
`;

const TitleText = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: ${Theme.Colors.textColor};
  line-height: 1.3;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SubtitleText = styled.div`
  font-size: 13px;
  color: ${Theme.Colors.warmGrey};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  padding-right: 12px;
  gap: 8px;
`;

const RefreshButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${Theme.Colors.lightestGrey};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Theme.Colors.warmGrey};
  transition: all 0.2s ease;
  font-size: 16px;

  &:hover {
    background: ${Theme.Colors.primary};
    color: white;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
    &:hover {
      background: ${Theme.Colors.lightestGrey};
      color: ${Theme.Colors.warmGrey};
    }
  }
`;

const ArrowIcon = styled.div`
  color: ${Theme.Colors.primary};
  font-size: 18px;
  opacity: 0.5;
  transition: all 0.3s ease;
`;

const LockOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 16px;
  cursor: pointer;
`;

export type HomeScreenSuggestionItemContentProps = {
  date?: Dayjs;
  image?: string;
  title?: string;
  onPress?: () => void;
  secondaryText?: string;
  onRefresh?: () => void;
  refreshDisabled?: boolean;
  locked?: boolean;
  onUnlock?: () => void;
  typeTag?: string;
};

export const HomeScreenSuggestionItemContent: React.FC<HomeScreenSuggestionItemContentProps> = observer(
  ({ locked, onUnlock, refreshDisabled, onRefresh, onPress, secondaryText, image, title, typeTag }) => {
    const subtitle = secondaryText ? secondaryText : '';

    return (
      <Container onClick={onPress}>
        <ImageContainer>
          {image ? (
            <Thumb className="suggestion-image" src={image} alt={title} />
          ) : (
            <PlaceholderImage>
              <i className="bi bi-lightning-charge-fill" />
            </PlaceholderImage>
          )}
        </ImageContainer>
        <Content>
          {typeTag && <TypeTag>{typeTag}</TypeTag>}
          {title && <TitleText>{title}</TitleText>}
          {subtitle && <SubtitleText>{subtitle}</SubtitleText>}
        </Content>
        <Actions>
          <RefreshButton
            onClick={(e) => {
              e.stopPropagation();
              onRefresh?.();
            }}
            disabled={refreshDisabled}
            aria-label="Refresh"
          >
            <i className="bi bi-arrow-clockwise" />
          </RefreshButton>
          <ArrowIcon className="arrow-icon">→</ArrowIcon>
        </Actions>
        {locked && (
          <LockOverlay
            onClick={(e) => {
              e.stopPropagation();
              onUnlock?.();
            }}
          >
            <i className="bi bi-lock-fill" style={{ fontSize: 28, color: Theme.Colors.warmGrey }} />
          </LockOverlay>
        )}
      </Container>
    );
  },
);
